import { createContext, useContext } from "react";
import type { ChatSummary } from "@ferrivia/api-contract";

export type ChatSummaryState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; summary: ChatSummary };

export interface ChatSummaryValue {
  state: ChatSummaryState;
  /** Poll now instead of waiting out the cadence. For the moments the app
      already knows the count just changed — reading the thread is the one
      today — so the surfaces carrying it do not sit stale for up to 45s. */
  refresh: () => void;
}

/* Context, provider and hooks are split across two files for the same reason
   useTabNavigation.ts is split from TabNavigation.tsx: a module exporting a
   component and plain hooks together breaks fast refresh. */
export const ChatSummaryContext = createContext<ChatSummaryValue | null>(null);

function useChatSummaryValue(): ChatSummaryValue {
  const context = useContext(ChatSummaryContext);
  if (!context) throw new Error("useChatSummary must be used within ChatSummaryProvider");
  return context;
}

/** The unread count, shared by every surface that shows it. */
export function useChatSummary(): ChatSummaryState {
  return useChatSummaryValue().state;
}

export function useRefreshChatSummary(): () => void {
  return useChatSummaryValue().refresh;
}
